import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { KTSVG } from '../../../../../_metronic/helpers'
import { useAuth } from '../../../../modules/auth'
import { Button, Form, Modal } from 'react-bootstrap'
import Select from 'react-select'
import moment from 'moment'
const API_URL = process.env.REACT_APP_API_URL
type Props = {
  callDetails: any
}

const CallBillingTab: React.FC<Props> = ({ callDetails }) => {
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const { currentUser } = useAuth()
  const code = callDetails?.id
  const hasLoaded = useRef(false)
  
  // Determine if call is onsite/third party (rates shown per hour) or OPI/VRI (rates shown per minute)
  const isOnsiteOrThirdParty = summary?.isOnsiteOrThirdParty ?? false

  const [showInterpreterModal, setShowInterpreterModal] = useState(false)
  const [showClientModal, setShowClientModal] = useState(false)
  const [interpreterOptions, setInterpreterOptions] = useState<any[]>([])
  const [showVoucherWarning, setShowVoucherWarning] = useState(false)
  const [showInvoiceWarning, setShowInvoiceWarning] = useState(false)

  const [editInterpreter, setEditInterpreter] = useState<any>({
    interpreterUserId: '',
    rate: '',
    startTime: '',
    endTime: '',
    adjustment: '',
    payInterpreter: true,
  })

  const [editClient, setEditClient] = useState<any>({
    rate: '',
    startTime: '',
    endTime: '',
    adjustment: '',
    chargeClient: true,
  })

  const canEdit =
    currentUser?.result?.userType === 'SYSTEM' ||
    currentUser?.result?.role === 'Admin' ||
    currentUser?.result?.role === 'PM'

  const isVoucherCreated = !!summary?.voucherNo || (summary?.isVoucherCreated === true && !!summary?.voucherNo)
  const isInvoiceCreated = !!summary?.invoiceNo || (summary?.isInvoiceCreated === true && !!summary?.invoiceNo)

  const money = (val: any) => {
    const num = Number(val)
    if (!Number.isFinite(num)) return '$0.00'
    return `$${num.toFixed(2)}`
  }

  const minutes = (val: any) => {
    const num = Number(val)
    if (!Number.isFinite(num)) return '0 min'
    return `${Math.round(num)} min`
  }

  const ratePerHour = (val: any) => {
    const num = Number(val)
    if (!Number.isFinite(num)) return isOnsiteOrThirdParty ? '$0.00 / hr' : '$0.00 / min'
    
    // For onsite/third party: convert per-minute rate to per-hour for display
    // For OPI/VRI: show per-minute rate as-is
    const displayRate = isOnsiteOrThirdParty ? num * 60 : num
    const unit = isOnsiteOrThirdParty ? 'hr' : 'min'
    return `$${displayRate.toFixed(2)} / ${unit}`
  }

  const buildRow = (label: string, value: React.ReactNode, bold?: boolean) => {
    return (
      <div className='d-flex justify-content-between align-items-center py-2 border-bottom border-gray-200'>
        <div className='text-gray-600'>{label}</div>
        <div className={bold ? 'fw-bold text-gray-900' : 'fw-semibold text-gray-800'}>{value}</div>
      </div>
    )
  }

  const load = async () => {
    if (!code || hasLoaded.current) return
    hasLoaded.current = true

    setLoading(true)
    try {
      const res = await axios.get(
        `${API_URL}/invoice/call-billing-summary/${code}`
      )
      setSummary(res?.data?.data || null)
    } finally {
      setLoading(false)
    }
  }

  const loadInterpreterOptions = async () => {
    if (interpreterOptions.length > 0) return
    try {
      const res = await axios.get(`${API_URL}/invoice/ddlist-interpreters`)
      const items = res?.data?.data || []
      setInterpreterOptions(
        items.map((x: any) => ({ value: x.key, label: x.value }))
      )
    } catch {
      setInterpreterOptions([])
    }
  }

  const parseTimeFromIsoOrDefault = (val: any) => {
    if (!val) return ''

    const d = new Date(val)
    if (Number.isNaN(d.getTime())) return ''
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${hh}:${mm}`
  }


  const openInterpreterEdit = async () => {
    await loadInterpreterOptions()
    // For display in edit modal, convert per-minute rate to per-hour for onsite/third party
    const displayRate = isOnsiteOrThirdParty && summary?.voucherRate 
      ? summary.voucherRate * 60 
      : summary?.voucherRate ?? ''
    setEditInterpreter({
      interpreterUserId: '',
      rate: String(displayRate),
      startTime: parseTimeFromIsoOrDefault(summary?.interpreterStartTime) || '',
      endTime: parseTimeFromIsoOrDefault(summary?.interpreterEndTime) || '',
      adjustment: String(summary?.voucherAdjustment ?? 0),
      payInterpreter: summary?.payInterpreter ?? true,
    })
    setShowInterpreterModal(true)
  }

  const openClientEdit = () => {
    // For display in edit modal, convert per-minute rate to per-hour for onsite/third party
    const displayRate = isOnsiteOrThirdParty && summary?.invoiceRate 
      ? summary.invoiceRate * 60 
      : summary?.invoiceRate ?? ''
    setEditClient({
      rate: String(displayRate),
      startTime: parseTimeFromIsoOrDefault(summary?.clientStartTime) || '',
      endTime: parseTimeFromIsoOrDefault(summary?.clientEndTime) || '',
      adjustment: String(summary?.invoiceAdjustment ?? 0),
      chargeClient: summary?.chargeClient ?? true,
    })
    setShowClientModal(true)
  }

  const saveInterpreterEdit = async (isWithoutUnlinking: boolean = false) => {
    // Convert per-hour rate back to per-minute for onsite/third party before sending to API
    let rateToSend = editInterpreter.rate === '' ? null : Number(editInterpreter.rate)
    if (rateToSend !== null && isOnsiteOrThirdParty) {
      rateToSend = rateToSend / 60  // Convert per-hour back to per-minute
    }
    
    const payload = {
      interpreterUserId: editInterpreter.interpreterUserId || null,
      interpreterRate: rateToSend,
      adjustment: editInterpreter.adjustment === '' ? null : Number(editInterpreter.adjustment),
      payInterpreter: !!editInterpreter.payInterpreter,
      isWithoutUnlinking
    }

    await axios.put(`${API_URL}/invoice/call/${code}/interpreter-billing`, payload)
    setShowInterpreterModal(false)
    hasLoaded.current = false
    await load()
  }

  const saveClientEdit = async (isWithoutUnlinking: boolean = false) => {
    // Convert per-hour rate back to per-minute for onsite/third party before sending to API
    let rateToSend = editClient.rate === '' ? null : Number(editClient.rate)
    if (rateToSend !== null && isOnsiteOrThirdParty) {
      rateToSend = rateToSend / 60  // Convert per-hour back to per-minute
    }
    
    const payload = {
      clientRate: rateToSend,
      adjustment: editClient.adjustment === '' ? null : Number(editClient.adjustment),
      chargeClient: !!editClient.chargeClient,
      isWithoutUnlinking
    }

    await axios.put(`${API_URL}/invoice/call/${code}/client-billing`, payload)
    setShowClientModal(false)
    hasLoaded.current = false
    await load()
  }

  useEffect(() => {
    load()
  }, [code])

  return (
    <div>
      {loading && <div className='text-muted'>Loading...</div>}
      {!loading && (
        <div className='card shadow-sm'>
          <div className='card-body'>
            <div className='d-flex align-items-start justify-content-between mb-6'>
              <div>
                <div className='d-flex align-items-center gap-3 mb-1'>
                  <div className='fw-semibold text-gray-700'>Status</div>
                  <div className='fw-semibold text-warning'>{summary?.status || summary?.callStatus || 'N/A'}</div>
                </div>
                <div className='text-gray-500' style={{ fontSize: '12px' }}>
                  {summary?.statusNote ||
                    summary?.note ||
                    'If status = Abandoned (For scheduled cases only) and interpreter started the session, interpreter duration defaults to 20 mins and client billable becomes 0.'}
                </div>
                <div className='d-flex align-items-center gap-2 mt-2'>
                  <span className='fw-semibold text-gray-700'>Invoice ID :</span>
                  {summary?.invoiceNo ? (
                    <a 
                      href={`/invoice/${summary?.invoiceHeaderCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-underline"
                    >
                      {summary?.invoiceNo}
                    </a>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
                <div className='d-flex align-items-center gap-2 mt-2'>
                  <span className='fw-semibold text-gray-700'>Voucher ID :</span>
                  {summary?.voucherNo ? (
                    <a 
                      href={`/voucher/${summary?.voucherHeaderCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-underline"
                    >
                      {summary?.voucherNo}
                    </a>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              </div>
            </div>

            <div className='row g-6'>
              <div className='col-12 col-md-6'>
                <div className='border rounded p-5 h-100'>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <div className='d-flex align-items-center gap-3'>
                      <div className='fw-bold text-gray-900'>Interpreter Payable</div>
                      { summary?.payInterpreter && <span className='badge badge-light-success'>Payable</span>}
                      {isVoucherCreated && <span className='badge badge-light-warning'>Voucher Created</span>}
                    </div>
                    {canEdit && (
                      <button type='button' className='btn btn-sm btn-icon btn-light' onClick={openInterpreterEdit}>
                        <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                      </button>
                    )}
                  </div>

                  {buildRow('Interpreter Name:', summary?.interpreterName || callDetails?.interpreterNameInline || summary?.voucherDetail?.name || 'N/A')}
                  {buildRow('Interpreter Rate:', ratePerHour(summary?.voucherRate ?? summary?.voucherDetail?.rate))}
                  {buildRow('Call Duration:', minutes(summary?.callDuration))}
                  {buildRow('Minimum Payable:', minutes(summary?.minimumPayable ?? summary?.voucherDetail?.minimumBillable ?? 0))}
                  {buildRow('Call Charge:', money(summary?.voucherCallCharge ?? summary?.voucherDetail?.amount ?? summary?.voucherDetail?.total))}
                  {buildRow('Adjustment:', money(summary?.voucherAdjustment ?? summary?.adjustment ?? 0))}
                  <div className='pt-3 d-flex justify-content-between align-items-center'>
                    <div className='fw-bold text-gray-900'>Payable Amount:</div>
                    <div className='fw-bold text-gray-900'>{money( summary?.voucherDetail?.total)}</div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='border rounded p-5 h-100'>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <div className='d-flex align-items-center gap-3'>
                      <div className='fw-bold text-gray-900'>Client Billable</div>
                      {summary?.chargeClient && <span className='badge badge-light-success'>Billable</span>}
                      {isInvoiceCreated && <span className='badge badge-light-warning'>Invoice Created</span>}
                    </div>
                    {canEdit && (
                      <button type='button' className='btn btn-sm btn-icon btn-light' onClick={openClientEdit}>
                        <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                      </button>
                    )}
                  </div>

                  {buildRow('Client Name:', summary?.clientName || callDetails?.companyName || summary?.invoiceDetail?.name || 'N/A')}
                  {buildRow('Client Rate:', ratePerHour(summary?.invoiceRate ?? summary?.invoiceDetail?.rate))}
                  {buildRow('Call Duration:', minutes(summary?.callDuration))}
                  {buildRow('Minimum Billable:', minutes(summary?.minimumBillable ?? summary?.invoiceDetail?.minimumBillable ?? 0))}
                  {buildRow('Call Charge:', money(summary?.invoiceCallCharge ?? summary?.invoiceDetail?.amount ?? summary?.invoiceDetail?.total))}
                  {buildRow('Adjustment:', money(summary?.invoiceAdjustment ?? summary?.adjustment ?? 0))}
                  <div className='pt-3 d-flex justify-content-between align-items-center'>
                    <div className='fw-bold text-gray-900'>Billable Amount:</div>
                    <div className='fw-bold text-gray-900'>{money( summary?.invoiceDetail?.total)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {showInterpreterModal && (
        <>
          {/* Backdrop */}
          <div
            className={`custom-modal-backdrop call-billing-edit-backdrop`}
            onClick={() => setShowInterpreterModal(false)}
            aria-hidden="true"
          />

          {/* Centered panel */}
          <div
            className="custom-modal-wrapper d-flex align-items-center justify-content-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="interpreter-edit-title"
          >
            <div className="card custom-modal-card shadow-lg" style={{ width: '640px', maxWidth: '95%' }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 id="interpreter-edit-title" className="mb-0">Edit Interpreter Payable</h5>
                {/* close button styled like bootstrap modal close */}
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowInterpreterModal(false)}
                />
              </div>

              <div className="card-body">
                <Form>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Interpreter Name</Form.Label>
                    <Form.Control
                      value={
                        summary?.interpreterName ||
                        callDetails?.interpreterNameInline ||
                        summary?.voucherDetail?.name ||
                        'N/A'
                      }
                      disabled
                    />
                  </Form.Group>

                  <div className="row">
                    <div className="col-12 col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Start Time</Form.Label>
                        <Form.Control value={moment(summary.startTime).format('hh:mm:ss A') || ''} disabled />
                      </Form.Group>
                    </div>
                    <div className="col-12 col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>End Time</Form.Label>
                        <Form.Control value={moment(summary.endTime).format('hh:mm:ss A') || ''} disabled />
                      </Form.Group>
                    </div>
                  </div>


                  <Form.Group className="mb-3">
                    <Form.Label>Interpreter Rate ({isOnsiteOrThirdParty ? '$/hr' : '$/min'})</Form.Label>
                    <Form.Control
                      type="number"
                      value={editInterpreter.rate}
                      onChange={(e) => setEditInterpreter((p: any) => ({ ...p, rate: e.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control value={`${Number(summary?.voucherDetail.duration || 0)} min`} disabled />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Minimum Payable</Form.Label>
                    <Form.Control value={`${Number(summary?.minimumPayable || 0)} min`} disabled />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Adjustment</Form.Label>
                    <Form.Control
                      type="number"
                      value={editInterpreter.adjustment}
                      onChange={(e) => setEditInterpreter((p: any) => ({ ...p, adjustment: e.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Pay Interpreter"
                      checked={!!editInterpreter.payInterpreter}
                      onChange={(e) => setEditInterpreter((p: any) => ({ ...p, payInterpreter: e.target.checked }))}
                    />
                  </Form.Group>
                </Form>
              </div>

              <div className="card-footer d-flex justify-content-between align-items-center">
                  <div>
                    {isVoucherCreated && (
                      <button
                        type="button"
                        className="btn btn-link btn-color-danger btn-active-color-danger p-0 fw-bold fs-7"
                        style={{ textDecoration: "underline", color: "#dc3545", background: "none", border: "none" }}
                        onClick={() => setShowVoucherWarning(true)}
                      >
                        Edit Without Unlinking
                      </button>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowInterpreterModal(false)}>
                      Cancel
                    </button>
                    {!isVoucherCreated && (
                      <button type="button" className="btn btn-primary" onClick={() => saveInterpreterEdit(false)}>
                        Save
                      </button>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </>
      )}


      {showClientModal && (
        <>
          {/* Backdrop */}
          <div
            className="custom-modal-backdrop call-billing-edit-backdrop"
            onClick={() => setShowClientModal(false)}
          />

          {/* Centered modal wrapper */}
          <div className="custom-modal-wrapper" role="dialog" aria-modal="true">
            <div className="card custom-modal-card shadow-lg" style={{ width: 640, maxWidth: '95%' }}>
              {/* Header */}
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Edit Client Billable</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowClientModal(false)}
                />
              </div>

              {/* Body */}
              <div className="card-body">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Client Name</Form.Label>
                    <Form.Control
                      value={summary?.clientName || callDetails?.companyName || ''}
                      disabled
                    />
                  </Form.Group>

                  <div className="row">
                    <div className="col-12 col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Start Time</Form.Label>
                        <Form.Control value={moment(summary.startTime).format('hh:mm:ss A') || ''} disabled />
                      </Form.Group>
                    </div>
                    <div className="col-12 col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>End Time</Form.Label>
                        <Form.Control value={moment(summary.endTime).format('hh:mm:ss A') || ''} disabled />
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Client Rate ({isOnsiteOrThirdParty ? '$/hr' : '$/min'})</Form.Label>
                    <Form.Control
                      type="number"
                      value={editClient.rate}
                      onChange={(e) =>
                        setEditClient((p: any) => ({ ...p, rate: e.target.value }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control
                      value={`${Number(summary?.invoiceDetail?.duration || 0)} min`}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Minimum Billable</Form.Label>
                    <Form.Control
                      value={`${Number(summary?.minimumBillable || 0)} min`}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Adjustment ($)</Form.Label>
                    <Form.Control
                      type="number"
                      value={editClient.adjustment}
                      onChange={(e) =>
                        setEditClient((p: any) => ({ ...p, adjustment: e.target.value }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Charge Client"
                      checked={!!editClient.chargeClient}
                      onChange={(e) =>
                        setEditClient((p: any) => ({
                          ...p,
                          chargeClient: e.target.checked,
                        }))
                      }
                    />
                  </Form.Group>
                </Form>
              </div>

              {/* Footer */}
              <div className="card-footer d-flex justify-content-between align-items-center">
                  <div>
                    {isInvoiceCreated && (
                      <button
                        type="button"
                        className="btn btn-link btn-color-danger btn-active-color-danger p-0 fw-bold fs-7"
                        style={{ textDecoration: "underline", color: "#dc3545", background: "none", border: "none" }}
                        onClick={() => setShowInvoiceWarning(true)}
                      >
                        Edit Without Unlinking
                      </button>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowClientModal(false)}>
                      Cancel
                    </button>
                    {!isInvoiceCreated && (
                      <button type="button" className="btn btn-primary" onClick={() => saveClientEdit(false)}>
                        Save
                      </button>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </>
      )}

      {/* Voucher Warning Modal */}
      {showVoucherWarning && (
        <>
          <div
            className="custom-modal-backdrop call-billing-edit-backdrop"
            style={{ zIndex: 1060 }}
            onClick={() => setShowVoucherWarning(false)}
            aria-hidden="true"
          />
          <div className="custom-modal-wrapper d-flex align-items-center justify-content-center" role="dialog" aria-modal="true" style={{ zIndex: 1061 }}>
            <div className="card custom-modal-card shadow-lg" style={{ width: '500px', maxWidth: '95%' }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-danger">Warning</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowVoucherWarning(false)}
                />
              </div>
              <div className="card-body">
                <p className="mb-0 fw-semibold">Edit without un-linking will make changes in existing voucher. Please ensure reconciliation at your end.</p>
              </div>
              <div className="card-footer d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowVoucherWarning(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowVoucherWarning(false);
                    saveInterpreterEdit(true);
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Invoice Warning Modal */}
      {showInvoiceWarning && (
        <>
          <div
            className="custom-modal-backdrop call-billing-edit-backdrop"
            style={{ zIndex: 1060 }}
            onClick={() => setShowInvoiceWarning(false)}
            aria-hidden="true"
          />
          <div className="custom-modal-wrapper d-flex align-items-center justify-content-center" role="dialog" aria-modal="true" style={{ zIndex: 1061 }}>
            <div className="card custom-modal-card shadow-lg" style={{ width: '500px', maxWidth: '95%' }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-danger">Warning</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowInvoiceWarning(false)}
                />
              </div>
              <div className="card-body">
                <p className="mb-0 fw-semibold">Edit without un-linking will make changes in existing invoice. Please ensure reconciliation at your end.</p>
              </div>
              <div className="card-footer d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowInvoiceWarning(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowInvoiceWarning(false);
                    saveClientEdit(true);
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  )
}

export default CallBillingTab





